import { useState, useMemo } from 'react'
import Filters from './Filters'
import FlagsTable from './FlagsTable'
import './Heatmap.css'

function Heatmap({ data, onFlagClick }) {
  const { totalFlags, departmentBreakdown, flags, departments } = data || {}
  const [filters, setFilters] = useState({
    status: 'all',
    severity: 'all',
    category: 'all',
    department: 'all',
    reporter: 'all'
  })

  const deptBreakdown = useMemo(() => {
    if (!departmentBreakdown) return {}
    try {
      return typeof departmentBreakdown === 'string' 
        ? JSON.parse(departmentBreakdown) 
        : departmentBreakdown
    } catch {
      return {}
    }
  }, [departmentBreakdown])

  const filteredFlags = useMemo(() => {
    if (!flags) return []
    
    return flags.filter(flag => {
      // Exclude resolved flags from escalations screen
      if (flag.status === 'resolved') {
        return false
      }
      
      if (filters.status !== 'all' && flag.status !== filters.status) {
        return false
      }
      if (filters.severity !== 'all' && flag.severity !== filters.severity) {
        return false
      }
      if (filters.category !== 'all' && flag.category !== filters.category) {
        return false
      }
      if (filters.department !== 'all') {
        const flagDept = flag.department || 'Unknown'
        if (flagDept !== filters.department) {
          return false
        }
      }
      return true
    })
  }, [flags, filters])

  const getHeatColor = (count, max) => {
    if (count === 0) return '#e8ede9'
    const intensity = count / max
    if (intensity > 0.7) return '#dc2626'
    if (intensity > 0.4) return '#f59e0b'
    return '#6b8e6f'
  }

  // Calculate department counts from non-resolved flags only
  const departmentsToShow = useMemo(() => {
    if (!departments || departments.length === 0) return []
    if (!flags || flags.length === 0) {
      // If no flags, show all departments with 0
      return departments.map(dept => ({ name: dept, count: 0 }))
    }
    
    // Count flags per department, excluding resolved flags
    const deptCounts = {}
    flags.forEach(flag => {
      // Don't count resolved flags
      if (flag.status === 'resolved') {
        return
      }
      const dept = flag.department || 'Unknown'
      deptCounts[dept] = (deptCounts[dept] || 0) + 1
    })
    
    // For each department in config, get its count (or 0 if no flags)
    return departments.map(dept => ({
      name: dept,
      count: deptCounts[dept] || 0
    }))
  }, [departments, flags])

  const maxCount = Math.max(...departmentsToShow.map(d => d.count), 1)

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  const removeFilter = (key) => {
    setFilters(prev => ({ ...prev, [key]: 'all' }))
  }

  const clearAllFilters = () => {
    setFilters({
      status: 'all',
      severity: 'all',
      category: 'all',
      department: 'all',
      reporter: 'all'
    })
  }

  if (totalFlags === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">✅</div>
        <h3>No flags raised yet</h3>
        <p>When employees raise concerns through Olivea, they'll appear here.</p>
      </div>
    )
  }

  return (
    <>
      <div className="heatmap-container">
        <div className="heatmap-title">Flags by Department</div>
        <div className="heatmap-grid">
          {departmentsToShow.map(({ name, count }) => (
            <div
              key={name}
              className={`heatmap-cell ${filters.department === name ? 'selected' : ''}`}
              style={{ background: getHeatColor(count, maxCount) }}
              onClick={() => updateFilter('department', name)}
            >
              <div className="dept-name">{name}</div>
              <div className="dept-count">{count}</div>
              <div className="tooltip">
                {name}: {count} flag{count !== 1 ? 's' : ''}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Filters
        filters={filters}
        flags={flags || []}
        departments={departments || []}
        reporters={data?.reporters || []}
        onFilterChange={updateFilter}
        onRemoveFilter={removeFilter}
        onClearAll={clearAllFilters}
      />

      <FlagsTable
        flags={filteredFlags}
        totalFlags={filteredFlags.length}
        onFlagClick={onFlagClick}
      />
    </>
  )
}

export default Heatmap

